import { Button, Container } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import 'components/molecules/popup/file-preview/FilePreviewPopup.scss';
import { useQueryClient } from '@tanstack/react-query';
import { useGetContract } from 'services/contract';
import {
  useDeleteContractPdfFile,
  useGetContractPdfFile,
  usePostContractPdfFile,
  usePutContractPdfFile,
} from 'services/contract/pdf';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PiFilePdf, PiTrash } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getFileExtension } from 'utils/global/file.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { useCustomParamId } from 'hooks/useParamId';

const ContractPdfTab = () => {
  const contractId = useCustomParamId('contractId');
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const contract = useGetContract(contractId, undefined, !!contractId);

  const deletePdf = useDeleteContractPdfFile();
  const postPdf = usePostContractPdfFile();
  const putPdf = usePutContractPdfFile();

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const hasExistingPdf = !!contract.data?.pdfUrl;
  const pdfQueryKey = useMemo(
    () => ['contract-pdf-file', contractId] as const,
    [contractId]
  );

  const shouldFetchServerPdf = !!contractId && hasExistingPdf && !file;
  const pdfFileQuery = useGetContractPdfFile(contractId, shouldFetchServerPdf);

  const localPreviewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file]
  );
  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const [serverPreviewUrl, setServerPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pdfFileQuery.data) {
      setServerPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pdfFileQuery.data);
    setServerPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfFileQuery.data]);

  const previewUrl = localPreviewUrl ?? serverPreviewUrl;
  const showPdfPreview = !!file || hasExistingPdf;

  const isSaving = putPdf.isPending || postPdf.isPending;

  const fileName = file
    ? file.name
    : `${contract.data?.title ?? 'contract'}.pdf`;

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.currentTarget.files?.[0];
    if (!selectedFile) return;

    const extension = getFileExtension(selectedFile).toLowerCase();
    if (extension !== 'pdf') {
      toast.error(t('formik-error.invalid-file-extension'));
      return;
    }

    const uploadSelectedFile = async () => {
      if (!contractId) return;

      setFile(selectedFile);
      const version = contract.data?.version ?? 0;
      const payload = { id: contractId, file: selectedFile, version };

      try {
        if (hasExistingPdf) {
          await putPdf.mutateAsync(payload);
        } else {
          await postPdf.mutateAsync(payload);
        }

        setFile(null);
        await contract.refetch();
        await queryClient.invalidateQueries({ queryKey: pdfQueryKey });
      } catch (error) {
        onErrorShowAllErrorsToast(error as Error);
      }
    };

    void uploadSelectedFile();
    event.currentTarget.value = '';
  };

  const onDeletePdf = async (actions: PopupActions) => {
    try {
      await deletePdf.mutateAsync({
        id: contractId,
        version: contract.data?.version,
      });

      setFile(null);
      await contract.refetch();
      await queryClient.invalidateQueries({ queryKey: pdfQueryKey });
      actions.close();
    } catch (error) {
      onErrorShowAllErrorsToast(error as Error);
    }
  };

  return (
    <div className='flex flex-col w-full gap-4'>
      <Container className='self-start'>
        <Container.Header
          title={
            <>
              <PiFilePdf />
              PDF
            </>
          }>
          <div className='flex m-1 gap-1'>
            <Button
              isClear
              onClick={() => inputRef.current?.click()}
              disabled={isSaving || contract.isLoading}>
              <Button.Icon icon={<PiFilePdf />} />
              <Button.Text
                text={
                  hasExistingPdf
                    ? t('contract-template-tabs.update-pdf')
                    : t('contract-template-tabs.add-pdf')
                }
              />
            </Button>

            <input
              ref={inputRef}
              className='hidden'
              type='file'
              accept='.pdf,application/pdf'
              onChange={onInputChange}
            />

            {hasExistingPdf && (
              <MagellanPopups.DeleteWarning
                name={contract.data?.title}
                transI18nKey='popup.delete-popup-pdf-file'
                removeLoading={isSaving}
                onRemove={onDeletePdf}
                trigger={
                  <Button
                    type='button'
                    isClear
                    variant={BUTTON_VARIANT.remove}
                    disabled={isSaving}>
                    <Button.Icon icon={<PiTrash />} />
                    <Button.Text
                      text={t('contract-template-tabs.delete-pdf')}
                    />
                  </Button>
                }
              />
            )}
          </div>
        </Container.Header>

        <Container.Content className='flex flex-col p-0 rounded-b-md overflow-hidden'>
          <div className='flex flex-row justify-between items-start gap-4 p-6 pb-0'>
            <div className='flex flex-col gap-3 min-w-0 flex-1' />
          </div>

          {!showPdfPreview && (
            <div className='flex items-center justify-center p-12 w-full h-full'>
              <div className='opacity-50'>
                {t('contract-template-tabs.no-pdf-file')}
              </div>
            </div>
          )}

          {showPdfPreview && (
            <div className='file-preview-container w-full max-w-full h-[636px]'>
              {shouldFetchServerPdf && pdfFileQuery.isLoading ? (
                <div className='flex items-center justify-center p-12 w-full h-full'>
                  <div className='opacity-50'>{t('table.isLoading')}</div>
                </div>
              ) : shouldFetchServerPdf && pdfFileQuery.isError ? (
                <div className='flex items-center justify-center p-12 w-full h-full'>
                  <div className='opacity-50'>{t('toast.not-found')}</div>
                </div>
              ) : previewUrl ? (
                <iframe
                  src={previewUrl}
                  className='file-preview-iframe'
                  title={fileName}
                />
              ) : (
                <div className='flex items-center justify-center p-12 w-full h-full'>
                  <div className='opacity-50'>{t('table.noData')}</div>
                </div>
              )}
            </div>
          )}
        </Container.Content>
      </Container>
    </div>
  );
};

export default ContractPdfTab;
